import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_AI_API_URL || '/api/ai';

interface KPI { total_conversations: number; ai_resolved: number; escalated: number; agent_resolved: number; avg_response_time_ms: number; ai_containment_rate: number; escalation_rate: number; avg_satisfaction?: number; total_messages: number; avg_messages_per_conversation: number; }
interface TimeSeries { period: string; total: number; ai_resolved: number; escalated: number; }
interface ReportData { kpis: KPI; time_series: TimeSeries[]; by_channel: Record<string, number>; by_status: Record<string, number>; }

const CHANNEL_COLORS = ['#8b5cf6','#10b981','#f59e0b','#3b82f6','#ef4444'];

export default function Reports() {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [channel, setChannel] = useState('');
  const [granularity, setGranularity] = useState('day');
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports`, {
        params: { date_from: dateFrom, date_to: dateTo, ...(channel && { channel }), granularity },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, channel, granularity, token]);

  useEffect(() => { load(); }, [load]);

  const kpi = data?.kpis;
  const ts = data?.time_series || [];
  const maxTotal = Math.max(...ts.map(t => t.total), 1);

  return (
    <div style={{ padding: 24, background: '#0f172a', minHeight: 'calc(100vh - 64px)', color: '#e2e8f0' }}>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>AI Reports & Analytics</div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', background: '#1e293b', padding: '14px 16px', borderRadius: 12, border: '1px solid #334155' }}>
        <label style={labelStyle}>From <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} /></label>
        <label style={labelStyle}>To <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} /></label>
        <label style={labelStyle}>Channel
          <select value={channel} onChange={e => setChannel(e.target.value)} style={inputStyle}>
            <option value="">All</option>
            {['web_chat','whatsapp','sms','email','voice'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Granularity
          <select value={granularity} onChange={e => setGranularity(e.target.value)} style={inputStyle}>
            {['hour','day','week','month'].map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <button onClick={load} disabled={loading} style={btnPrimary}>{loading ? 'Loading…' : '↻ Refresh'}</button>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Conversations', value: kpi.total_conversations, color: '#8b5cf6' },
            { label: 'AI Resolved', value: kpi.ai_resolved, color: '#10b981' },
            { label: 'Escalated', value: kpi.escalated, color: '#f59e0b' },
            { label: 'Agent Resolved', value: kpi.agent_resolved, color: '#6366f1' },
            { label: 'AI Containment', value: `${kpi.ai_containment_rate}%`, color: '#10b981' },
            { label: 'Escalation Rate', value: `${kpi.escalation_rate}%`, color: '#ef4444' },
            { label: 'Total Messages', value: kpi.total_messages, color: '#3b82f6' },
            { label: 'Avg Messages/Conv', value: kpi.avg_messages_per_conversation.toFixed(1), color: '#64748b' },
            { label: 'Avg Satisfaction', value: kpi.avg_satisfaction ? `${kpi.avg_satisfaction.toFixed(1)} ⭐` : 'N/A', color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ background: '#1e293b', border: `1px solid ${s.color}25`, borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        {/* Time series bar chart */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>Conversations Over Time</div>
          {ts.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#334155', padding: 40 }}>No data for selected range</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, overflowX: 'auto' }}>
              {ts.map(t => (
                <div key={t.period} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36 }}>
                  <div style={{ width: 28, position: 'relative', height: Math.max((t.total / maxTotal) * 120, 2), background: '#8b5cf6', borderRadius: '3px 3px 0 0' }} title={`Total: ${t.total}`} />
                  <div style={{ width: 28, height: Math.max((t.ai_resolved / maxTotal) * 120, 2), background: '#10b981', borderRadius: 0, marginTop: 2 }} title={`AI Resolved: ${t.ai_resolved}`} />
                  <div style={{ fontSize: 9, color: '#475569', marginTop: 4, transform: 'rotate(-45deg)', transformOrigin: 'left', whiteSpace: 'nowrap' }}>
                    {t.period.slice(0, 10)}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 20, fontSize: 11 }}>
            {[{ c: '#8b5cf6', l: 'Total' }, { c: '#10b981', l: 'AI Resolved' }].map(x => (
              <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, background: x.c, borderRadius: 2 }} />
                <span style={{ color: '#94a3b8' }}>{x.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By channel donut */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>By Channel</div>
          {Object.entries(data?.by_channel || {}).map(([ch, count], i) => {
            const total = Object.values(data?.by_channel || {}).reduce((a, b) => a + b, 0);
            return (
              <div key={ch} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: '#e2e8f0' }}>{ch}</span>
                  <span style={{ color: CHANNEL_COLORS[i % CHANNEL_COLORS.length], fontWeight: 700 }}>{count}</span>
                </div>
                <div style={{ height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${total ? (count / total) * 100 : 0}%`, background: CHANNEL_COLORS[i % CHANNEL_COLORS.length], borderRadius: 3 }} />
                </div>
              </div>
            );
          })}

          <div style={{ fontWeight: 700, marginTop: 20, marginBottom: 12 }}>By Status</div>
          {Object.entries(data?.by_status || {}).map(([st, count]) => (
            <div key={st} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
              <span style={{ color: '#94a3b8' }}>{st.replace('_', ' ')}</span>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#94a3b8' };
const inputStyle: React.CSSProperties = { background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 13, marginTop: 2 };
const btnPrimary: React.CSSProperties = { background: '#8b5cf6', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end' };
